import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  InputAdornment,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon, Search } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import ActionDialog from '../../../components/dialog/ActionDialog';
import BasicButton from '../../../components/button/BasicButton';
import ErrorState from '../../../components/common/ErrorStats';
import NoData from '../../../components/common/NoData';
import Pagination from '../../../components/common/Pagination';
import ProgressBar from '../../../components/common/ProgressBar';
import { adminApi } from '../../../api/admin';
import theme from '../../../theme';

const ROWS_PER_PAGE = 20;

const HEAD_CELLS = [
  { id: 'ARTIST_NAME', label: '아티스트', sortable: true },
  { id: 'ALBUM_TITLE', label: '앨범', sortable: true },
  { id: 'RELEASE_DATE', label: '발매일', sortable: true },
  { id: 'VIEW_COUNT', label: '조회수', sortable: true },
  { id: 'REVIEW_COUNT', label: '리뷰수', sortable: true },
  { id: 'BOOKMARK_COUNT', label: '저장수', sortable: true },
  { id: 'reason', label: '숨김 사유', sortable: false },
];

const getArtistUrl = (album) => {
  if (album.artistSlug) return `/artists/${album.artistSlug}`;
  if (album.artistSource && album.artistSourceId) {
    return `/artists/${album.artistSource.toLowerCase()}/${album.artistSourceId}`;
  }
  return null;
};

const getAlbumUrl = (album) =>
  album.albumSlug ? `/reviews/${album.albumSlug}` : `/reviews/local/${album.albumId}`;

const HiddenAlbumManagement = ({ searchParams, updateURL, onCountsChange }) => {
  const debouncedSearchTerm = searchParams.get('search') || '';
  const sortField = searchParams.get('sortField') || 'RELEASE_DATE';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const [searchTerm, setSearchTerm] = useState(debouncedSearchTerm);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({ totalPages: 1, totalItems: 0 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [selectedAlbumIds, setSelectedAlbumIds] = useState([]);
  const [isUnhideDialogOpen, setIsUnhideDialogOpen] = useState(false);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchAlbums = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.getHiddenAlbums(
          currentPage,
          ROWS_PER_PAGE,
          debouncedSearchTerm,
          sortField,
          sortOrder,
        );
        setAlbums(response.data);
        setPaginationInfo(response.meta.pagination);
      } catch (err) {
        setError(err);
        setAlbums([]);
        setPaginationInfo({ totalPages: 1, totalItems: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchAlbums();
  }, [currentPage, debouncedSearchTerm, refetchTrigger, sortField, sortOrder]);

  const handleSearchClick = () => {
    updateURL({ search: searchTerm || null, page: null });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') handleSearchClick();
  };

  const handleSort = (field) => {
    const isCurrentField = sortField === field;
    const newSortOrder = isCurrentField && sortOrder === 'desc' ? 'asc' : 'desc';
    updateURL({ sortField: field, sortOrder: newSortOrder, page: null });
  };

  const handleSelectAllClick = (event) => {
    const currentPageAlbumIds = albums.map((album) => album.albumId);
    if (event.target.checked) {
      setSelectedAlbumIds((prev) => [...new Set([...prev, ...currentPageAlbumIds])]);
      return;
    }
    setSelectedAlbumIds((prev) => prev.filter((id) => !currentPageAlbumIds.includes(id)));
  };

  const handleSelectClick = (event, id) => {
    event.stopPropagation();
    setSelectedAlbumIds((prev) =>
      prev.includes(id) ? prev.filter((albumId) => albumId !== id) : [...prev, id],
    );
  };

  const handleChangePage = (event, newPage) => {
    updateURL({ page: newPage });
  };

  const handleUnhide = async () => {
    await adminApi.unhideAlbums(selectedAlbumIds);
    setIsUnhideDialogOpen(false);
    setSelectedAlbumIds([]);
    setRefetchTrigger((c) => c + 1);
    onCountsChange?.();
  };

  const numSelectedOnPage = useMemo(
    () => albums.filter((album) => selectedAlbumIds.includes(album.albumId)).length,
    [albums, selectedAlbumIds],
  );

  const isAllOnPageSelected = albums.length > 0 && numSelectedOnPage === albums.length;
  const isIndeterminate = numSelectedOnPage > 0 && !isAllOnPageSelected;
  const hasSelection = selectedAlbumIds.length > 0;
  const cellStyle = { borderRight: `1px solid ${theme.palette.grey.line}` };
  const textStyle = { fontSize: 14, color: theme.palette.black[9] };
  const buttonStyle = { typography: 'body3', fontWeight: 'medium', padding: '12px 16px' };

  return (
    <>
      <Stack spacing="8px" sx={{ mb: '30px' }}>
        <Typography fontSize={14} color={theme.palette.black[7]}>
          앨범 검색
        </Typography>
        <Box
          sx={{
            p: 1.5,
            width: '100%',
            border: `1px solid ${theme.palette.grey.line}`,
            borderRadius: 0.5,
          }}
        >
          <Autocomplete
            options={[]}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="아티스트 또는 앨범 검색"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search
                        sx={{
                          width: { xs: 20, lg: 16 },
                          height: { xs: 20, lg: 16 },
                          color: theme.palette.black[1],
                        }}
                      />
                    </InputAdornment>
                  ),
                  onKeyPress: handleSearchKeyDown,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '16px', lg: '14px' },
                    padding: '0 !important',
                  },
                  '& .MuiInputBase-root': {
                    paddingRight: '0 !important',
                  },
                }}
              />
            )}
            value={searchTerm}
            inputValue={searchTerm}
            onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
          />
        </Box>
      </Stack>

      <BasicButton
        variant="outlined"
        onClick={() => setIsUnhideDialogOpen(true)}
        disabled={!hasSelection}
        sx={{ ...buttonStyle, mb: { xs: '20px', md: '16px' } }}
      >
        숨김 해제
      </BasicButton>

      <ProgressBar isLoading={loading}>
        {error ? (
          <ErrorState />
        ) : !loading && albums.length === 0 ? (
          <NoData message="숨긴 앨범이 없습니다" />
        ) : (
          <Stack spacing={7.5}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                    <TableCell padding="checkbox" sx={cellStyle}>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllOnPageSelected}
                        onChange={handleSelectAllClick}
                        size="small"
                        sx={{
                          color: theme.palette.grey.line,
                          '&.Mui-checked': { color: '#f16438' },
                          '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                        }}
                      />
                    </TableCell>
                    {HEAD_CELLS.map((headCell, index) => (
                      <TableCell
                        key={headCell.id}
                        align="center"
                        sortDirection={sortField === headCell.id ? sortOrder : false}
                        sx={{
                          whiteSpace: 'nowrap',
                          borderRight:
                            index === HEAD_CELLS.length - 1
                              ? 'none'
                              : `1px solid ${theme.palette.grey.line}`,
                        }}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={sortField === headCell.id}
                            direction={sortField === headCell.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(headCell.id)}
                            IconComponent={ArrowDropDownIcon}
                            sx={{
                              '&.Mui-active .MuiTableSortLabel-icon': {
                                color: theme.palette.black[3],
                              },
                            }}
                          >
                            <Typography fontSize={14} color={theme.palette.black[4]}>
                              {headCell.label}
                            </Typography>
                          </TableSortLabel>
                        ) : (
                          <Typography fontSize={14} color={theme.palette.black[4]}>
                            {headCell.label}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {albums.map((album) => {
                    const artistUrl = getArtistUrl(album);
                    const isItemSelected = selectedAlbumIds.includes(album.albumId);
                    return (
                      <TableRow
                        key={album.albumId}
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox" sx={cellStyle}>
                          <Checkbox
                            checked={isItemSelected}
                            onClick={(event) => handleSelectClick(event, album.albumId)}
                            size="small"
                            sx={{
                              color: theme.palette.grey.line,
                              '&.Mui-checked': { color: '#f16438' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, minWidth: 240 }}>
                          {artistUrl ? (
                            <Link
                              component={RouterLink}
                              to={artistUrl}
                              sx={{ textDecoration: 'none' }}
                            >
                              <Typography {...textStyle}>{album.artistName}</Typography>
                            </Link>
                          ) : (
                            <Typography {...textStyle}>{album.artistName || '-'}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, minWidth: 360 }}>
                          <Link
                            component={RouterLink}
                            to={getAlbumUrl(album)}
                            sx={{ textDecoration: 'none' }}
                          >
                            <Typography {...textStyle}>{album.albumTitle}</Typography>
                          </Link>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>{album.releaseDate || '-'}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>
                            {Number(album.viewCount || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>
                            {Number(album.reviewCount || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>
                            {Number(album.bookmarkCount || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography {...textStyle}>Apple Music DJ MIX</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={paginationInfo.totalPages}
              page={currentPage}
              onChange={handleChangePage}
            />
          </Stack>
        )}
      </ProgressBar>

      <ActionDialog
        open={isUnhideDialogOpen}
        onClose={() => setIsUnhideDialogOpen(false)}
        onConfirm={handleUnhide}
        confirmText="확인"
      >
        <Typography sx={{ textAlign: 'center' }}>
          선택한 앨범을 최신 앨범 목록에 다시 노출하시겠습니까?
        </Typography>
      </ActionDialog>
    </>
  );
};

export default HiddenAlbumManagement;
